import * as XLSX from 'xlsx';

/**
 * Download a table as a real .xlsx workbook, with chosen columns forced to text.
 *
 * These exports used to be hand-built CSV, which cannot survive the round trip an
 * operator actually performs — open in Excel, edit, re-import into Manage Fleets:
 *
 *   - Excel re-types every CSV field that looks numeric. An all-digit serial such as
 *     0071043707 came back as 71043707, and a longer one as 71045000000: the leading
 *     zeros are gone and the value no longer matches anything in FTCloud or IoT.
 *     Serials containing a letter (007100576C) were untouched, which is why only some
 *     rows looked broken.
 *   - Wrapping cells in double quotes does not prevent that — Excel converts quoted
 *     numerics just the same — and the quotes then leaked into the importer, which
 *     stored serials as the literal string "0071004A83".
 *
 * A worksheet cell carries its own type, so a serial written as a text cell stays
 * exactly what it was, and there are no quotes for anything downstream to mistake for
 * part of the value.
 *
 * @param {object}   options
 * @param {string}   options.filename     File name including the .xlsx extension.
 * @param {string[]} options.headers      Header row.
 * @param {Array[]}  options.rows         Data rows, aligned with `headers`.
 * @param {string[]} [options.textColumns] Header names to pin to text (e.g. serials).
 * @param {string}   [options.sheetName]  Worksheet name; Excel caps this at 31 chars.
 */
export function downloadSheet({ filename, headers, rows, textColumns = [], sheetName = 'Export' }) {
  const textIndexes = new Set(
    textColumns
      .map(name => headers.indexOf(name))
      .filter(index => index >= 0)
  );

  const body = rows.map(row =>
    row.map((cell, index) => {
      if (cell === null || cell === undefined) return '';
      return textIndexes.has(index) ? String(cell) : cell;
    })
  );

  const sheet = XLSX.utils.aoa_to_sheet([headers, ...body]);

  // aoa_to_sheet already types a JS string as text, but say it explicitly: a value that
  // reached us as a number would otherwise be written as one and lose its leading zeros
  // all over again.
  textIndexes.forEach(columnIndex => {
    for (let rowIndex = 1; rowIndex <= body.length; rowIndex += 1) {
      const ref = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = sheet[ref];
      if (!cell) continue;
      cell.t = 's';
      cell.v = String(cell.v ?? '');
      delete cell.z;
      delete cell.w;
    }
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName.slice(0, 31));
  XLSX.writeFile(workbook, filename);
}

export default downloadSheet;
